import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, Stack, TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CiSearch } from "react-icons/ci";
import {
  useLazySearchSessionProgramsQuery, useSaveProgramAssignmentsMutation,
  useValidateProgramAssignmentsMutation, useLazySearchAbaStaffQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { LegacyTokens, TypeScale, B13, B13Ink, tint12 } from "../../../theme/b13Tokens";
import { AppDatePicker } from "../../../Components/DatePicker";

/**
 * Batch 49 §1 — Assign ABA Program dialog (was a Drawer through Batch 45).
 *
 * Kept the filename + default export so every call site continues to work.
 * The dialog is centred at ~880px with Create Program's layout language:
 * page-header row, grouped sections, generous spacing, footer actions.
 *
 * §1.1 — hits the new /session-data/search/programs?assignableFor=<id>
 * path so the picker returns programs NOT yet linked to this student,
 * bypassing the 3-char guard so the drawer has options as soon as it
 * opens.
 */
type Props = {
  open: boolean;
  student: {
    id: number; label: string; reference_id?: string;
    grade_id?: number | null; grade_label?: string | null;
    location_id?: number | null;
    assigned_staff?: string; active_programs_count?: number; aba?: boolean;
  };
  defaultStaff?: { id: number; label: string } | null;
  defaultDate?: string;
  onClose: () => void;
  onAssigned: (info: { programId: number; staffId: number | null; programName?: string; staffName?: string | null }) => void;
};

type ProgramRow = { id: number; label: string; category?: string | null; treatmentAreaName?: string | null; folderId?: number | null };

export default function AssignAbaProgramDrawer({ open, student, defaultStaff, defaultDate, onClose, onAssigned }: Props) {
  const { showMessage, showErrorMessage } = useNotifier();
  const [searchPrograms] = useLazySearchSessionProgramsQuery();
  const [searchAbaStaff] = useLazySearchAbaStaffQuery();
  const [validate] = useValidateProgramAssignmentsMutation();
  const [save, { isLoading: saving }] = useSaveProgramAssignmentsMutation();

  const [q, setQ] = useState("");
  const [progs, setProgs] = useState<ProgramRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ProgramRow | null>(null);
  const [staff, setStaff] = useState<any>(defaultStaff ?? null);
  const [staffOpts, setStaffOpts] = useState<any[]>(defaultStaff ? [defaultStaff] : []);
  const [startDate, setStartDate] = useState<string>(defaultDate || new Date().toISOString().slice(0, 10));
  const [reviewDate, setReviewDate] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [validationRow, setValidationRow] = useState<{ blocking: boolean; errors: string[]; warnings: string[] } | null>(null);

  // Reset transient state whenever the dialog reopens for a different student.
  useEffect(() => {
    if (!open) return;
    setQ("");
    setSelected(null);
    setStaff(defaultStaff ?? null);
    setStaffOpts(defaultStaff ? [defaultStaff] : []);
    setStartDate(defaultDate || new Date().toISOString().slice(0, 10));
    setReviewDate("");
    setNotes("");
    setValidationRow(null);
  }, [open, student?.id, defaultStaff, defaultDate]);

  // Load eligible Programs when the dialog opens.
  useEffect(() => {
    if (!open || !student?.id) return;
    setLoading(true);
    searchPrograms({ assignableFor: student.id } as any)
      .then((r: any) => setProgs(((r?.data?.data ?? []) as ProgramRow[])))
      .finally(() => setLoading(false));
  }, [open, student?.id, searchPrograms]);

  // Load initial ABA-staff options when the dialog opens. Without this,
  // the Autocomplete only fetches via onInputChange (user typing), so
  // opening the dropdown by clicking the arrow shows an empty list even
  // when the endpoint has data. Same one-off-on-open pattern as the
  // Programs fetch above.
  useEffect(() => {
    if (!open || !student?.id) return;
    searchAbaStaff({ q: "", student_id: student.id } as any)
      .then((r: any) => setStaffOpts(r?.data?.data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student?.id]);

  // Live search once the user types (no 3-char guard — server allows single char in assignableFor mode).
  useEffect(() => {
    if (!open || !student?.id) return;
    const t = setTimeout(() => {
      setLoading(true);
      searchPrograms({ assignableFor: student.id, q } as any)
        .then((r: any) => setProgs(((r?.data?.data ?? []) as ProgramRow[])))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, student?.id, searchPrograms]);

  // Re-validate on key changes.
  useEffect(() => {
    if (!selected?.id) { setValidationRow(null); return; }
    validate({ program_id: selected.id, assignments: [{
      studentId: student.id, staffId: staff?.id ?? null,
      startDate: startDate || null, reviewDate: reviewDate || null,
      notes: notes || null,
    }] }).unwrap().then((r: any) => {
      const row = (r?.data ?? []).find((x: any) => x.studentId === student.id);
      if (row) setValidationRow({ blocking: !!row.blocking, errors: row.errors ?? [], warnings: row.warnings ?? [] });
    }).catch(() => {});
  }, [selected?.id, staff?.id, startDate, reviewDate, notes, student?.id, validate]);

  const eligible = useMemo(() => progs.filter((p) => p?.id), [progs]);

  const submit = async () => {
    if (!selected?.id) { showErrorMessage("Select a Program to assign."); return; }
    try {
      const r: any = await save({ program_id: selected.id, assignments: [{
        studentId: student.id,
        staffId: staff?.id ?? null,
        startDate: startDate || null,
        reviewDate: reviewDate || null,
        notes: notes || null,
        status: "active",
      }] }).unwrap();
      if (r?.status) {
        showMessage("Program assigned to this Student.");
        onAssigned({
          programId: Number(selected.id), programName: selected.label,
          staffId: staff?.id ?? null, staffName: staff?.label ?? null,
        });
        onClose();
      } else {
        showErrorMessage(r?.message || "Assignment failed.");
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Assignment failed."); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: 880, maxWidth: "100%", borderRadius: `${LegacyTokens.radius.card}px` } }}>
      {/* Header — Create Program language. */}
      <DialogTitle sx={{ p: 3, pb: 2, borderBottom: `1px solid ${LegacyTokens.line}` }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{
            width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
            backgroundColor: tint12(B13.primary), color: B13.primary,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <AssignmentIndOutlinedIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography component="h2" sx={{ ...TypeScale.pageTitle, fontSize: 22, lineHeight: 1.2 }}>
              Assign ABA Program
            </Typography>
            <Typography sx={{ fontSize: 14, color: B13Ink.ink2, mt: 0.5, fontWeight: 600 }}>
              {student?.label}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {student?.aba && <SmallChip label="ABA / Special Program" />}
              {(student?.grade_label || student?.grade_id != null) &&
                <SmallChip label={`Grade ${student.grade_label ?? student.grade_id}`} />}
              {student?.location_id != null && <SmallChip label={`Loc ${student.location_id}`} />}
              {student?.assigned_staff && <SmallChip label={`Staff: ${student.assigned_staff}`} />}
              {typeof student?.active_programs_count === "number" && (
                <SmallChip label={`${student.active_programs_count} active Program${student.active_programs_count === 1 ? "" : "s"}`} />
              )}
            </Stack>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: B13Ink.ink3 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: LegacyTokens.surface }}>
        {/* Section 1 — Choose a program */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block" }}>Choose a Program</Typography>
          {/* Create-new escape hatch, adjacent to the picker (semantic:
              "if none fits, make one"). Secondary weight — outlined
              small button in the primary colour — clearly reads as an
              action, but subordinate to the footer's Assign Program.
              Someone who has scrolled the list and found nothing
              suitable should notice this without hunting. */}
          <Button
            size="small" variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => window.open(`/aba-therapy/programs/new?studentId=${student.id}`, "_blank", "noopener")}
            sx={{
              color: B13.primary, borderColor: tint12(B13.primary),
              textTransform: "none", fontWeight: 600,
              "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
            }}
          >
            Create New Program
          </Button>
        </Stack>
        <TextField
          size="small" fullWidth autoFocus
          placeholder="Search Programs the library"
          value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: B13Ink.ink3 }} /></InputAdornment>,
            endAdornment: loading ? <CircularProgress size={16} /> : undefined,
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              backgroundColor: LegacyTokens.surface,
              borderRadius: `${LegacyTokens.radius.control}px`,
              "& fieldset": { borderColor: LegacyTokens.line },
              "&:hover fieldset": { borderColor: B13.primary },
              "&.Mui-focused fieldset": { borderColor: B13.primary },
            },
          }}
        />
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={24} sx={{ color: B13.primary }} />
          </Box>
        ) : eligible.length === 0 ? (
          // §1.5 — honest empty state. Do not tell the user to "broaden
          // their search" when the search is empty; explain what is true.
          <Card variant="outlined" sx={{
            p: 3, textAlign: "center",
            borderColor: LegacyTokens.line, backgroundColor: LegacyTokens.canvas,
            borderRadius: `${LegacyTokens.radius.control}px`,
          }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink, mb: 0.5 }}>
              {q ? `No Programs match "${q}"` : "No Programs available to assign"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: B13Ink.ink3, mb: 2 }}>
              {q
                ? "Try a shorter term, or create a new Program below."
                : "Every Program in the library is already assigned to this Student. Create a new one to continue."}
            </Typography>
            <Button
              variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={() => window.open(`/aba-therapy/programs/new?studentId=${student.id}`, "_blank", "noopener")}
              sx={{
                color: B13.primary, borderColor: tint12(B13.primary),
                textTransform: "none", fontWeight: 600,
                "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
              }}
            >
              Create New Program
            </Button>
          </Card>
        ) : (
          <Stack
            spacing={1.25}
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              pr: 0.5,
              // Defined edge — a scrollable region without a boundary
              // reads as "the list ends here" and the last-visible row
              // gets cut mid-height. Border + rounded corners frame it.
              border: `1px solid ${LegacyTokens.line}`,
              borderRadius: `${LegacyTokens.radius.control}px`,
              p: 1,
              backgroundColor: LegacyTokens.canvas,
            }}
          >
            {eligible.map((p) => {
              const isSel = selected?.id === p.id;
              return (
                <Card
                  key={p.id} variant="outlined"
                  // Toggle-select: clicking the already-selected row
                  // clears the selection so the user can back out
                  // without closing the dialog.
                  onClick={() => setSelected((cur) => cur?.id === p.id ? null : p)}
                  sx={{
                    // Row rhythm — explicit vertical padding and
                    // minHeight so browser default line-heights on the
                    // stacked Typographies can't collapse into the
                    // padded area. The overlap symptom was that the
                    // subtitle bled into the sibling row's top edge;
                    // this locks in ~66px per row with a 10px gap.
                    px: 1.5, py: 1.25, minHeight: 60,
                    cursor: "pointer",
                    borderRadius: `${LegacyTokens.radius.control}px`,
                    borderColor: isSel ? B13.primary : LegacyTokens.line,
                    backgroundColor: isSel ? tint12(B13.primary) : LegacyTokens.surface,
                    display: "flex", alignItems: "center", gap: 1.5,
                    boxShadow: "none",
                    "&:hover": { borderColor: B13.primary },
                  }}
                >
                  <Box sx={{
                    width: 32, height: 32, borderRadius: `${LegacyTokens.radius.control}px`,
                    backgroundColor: isSel ? B13.primary : tint12(B13.primary),
                    color: isSel ? LegacyTokens.surface : B13.primary,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <DescriptionOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink, lineHeight: 1.35 }}
                      noWrap
                    >
                      {p.label}
                    </Typography>
                    {(p.treatmentAreaName || p.category) && (
                      <Typography
                        sx={{ fontSize: 12, color: B13Ink.ink3, lineHeight: 1.35, mt: 0.25 }}
                        noWrap
                      >
                        {p.treatmentAreaName || p.category}
                      </Typography>
                    )}
                  </Box>
                  {isSel && <CheckCircleIcon sx={{ color: B13.primary, fontSize: 20 }} />}
                </Card>
              );
            })}
          </Stack>
        )}

        <Divider sx={{ my: 3, borderColor: LegacyTokens.line }} />

        {/* Section 2 — Assignment Details, two-column */}
        <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1.5 }}>Assignment Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete size="small"
              value={staff} onChange={(_, v: any) => setStaff(v)}
              options={staffOpts}
              getOptionLabel={(o: any) => o?.label ?? ""}
              isOptionEqualToValue={(a: any, b: any) => a?.id === b?.id}
              onInputChange={(_, v) => searchAbaStaff({ q: v, student_id: student.id } as any).then((r: any) => setStaffOpts(r?.data?.data ?? []))}
              // Empty-state copy names the reason (location scoping +
              // ABA eligibility) and points at the safe action (leave
              // blank, assign later). Does NOT suggest editing the
              // student's location — that's a real data change with
              // downstream effects and disproportionate to a picker
              // empty state.
              noOptionsText="No ABA-eligible staff found for this student's location. You can leave this blank and assign staff later."
              renderInput={(pp) => (
                <TextField {...pp} label="Assigned ABA Staff"
                  placeholder="Type to search or leave blank"
                  InputLabelProps={{ shrink: true }} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} />
          <Grid item xs={12} md={6}>
            {/* §1.4 — AppDatePicker is the app's canonical date input;
               swaps the native browser calendar for the brand-consistent
               popover used everywhere else in the frontend. */}
            <AppDatePicker
              label="Start Date *"
              value={startDate}
              onChange={setStartDate}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AppDatePicker
              label="Review Date"
              value={reviewDate}
              onChange={setReviewDate}
              size="small"
              fullWidth
              minDate={startDate || undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField size="small" fullWidth multiline minRows={2} label="Assignment Notes"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>

        {validationRow && (validationRow.errors.length > 0 || validationRow.warnings.length > 0) && (
          <Box sx={{ mt: 2 }}>
            {validationRow.errors.map((e, i) => <Alert key={"e" + i} severity="error" sx={{ mb: 0.5 }}>{e}</Alert>)}
            {validationRow.warnings.map((w, i) => <Alert key={"w" + i} severity="warning" sx={{ mb: 0.5 }}>{w}</Alert>)}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 2,
        borderTop: `1px solid ${LegacyTokens.line}`,
        backgroundColor: B13.canvas,
        // Create-new moved to the picker header (next to "Choose a
        // Program"). Footer is Cancel + primary now — no third action
        // reading as equal weight to Assign.
        justifyContent: "flex-end",
        gap: 1,
      }}>
        <Button onClick={onClose} sx={{ color: B13Ink.ink3, textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" onClick={submit}
          disabled={saving || !selected?.id || (validationRow?.blocking ?? false)}
          sx={{
            backgroundColor: B13.primary, color: LegacyTokens.surface,
            textTransform: "none", fontWeight: 600, px: 2.5,
            "&:hover": { backgroundColor: B13.primary },
            "&.Mui-disabled": { backgroundColor: B13.canvas, color: B13Ink.ink3 },
          }}
        >
          {saving ? "Assigning…" : "Assign Program"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SmallChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip size="small" variant="outlined" label={label}
    sx={{ borderColor: LegacyTokens.line, color: B13Ink.ink3, fontSize: 11 }} />
);
